import React from "react";
import {
  Col,
  Logo,
  textVr,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiLink,
  UiPrimaryButton,
  UiText,
} from "~/components/elements";
import PasswordInput from "~/components/shared/PasswordInput";
import { useAppDispatch, useInput, useNotification, useRequestState } from "~/hooks";
import { resetPwd, verifyPasscodeResetPwd } from "~/redux/auth/thunk";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { Link } from "react-router-dom";
import { PAGE_URLS } from "~/constants";

export function SetNewPassword({
  onSuccess,
  code,
  userId,
}: {
  onSuccess: ({ email, userId }: { email?: string; userId: string }) => void;
  code: string;
  userId: string;
}) {
  const dispatch = useAppDispatch();
  const { addMessage } = useNotification(dispatch);
  const { loading } = useRequestState(verifyPasscodeResetPwd);

  const rules = {
    password(value: string) {
      if (value) {
        return { length: { minimum: 6 } };
      }
      return { presence: true };
    },
    confirmPassword(value: string) {
      if (value) {
        return {
          length: { minimum: 6 },
          equality: {
            attribute: "password",
            message: "^Passwords do not match!",
          },
        };
      }
      return { presence: true };
    },
  };

  const [inputs, setInputs, { validation }] = useInput({ password: "", confirmPassword: "" }, { rules });

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();
    if (validation.getErrors()) {
      return;
    }

    const resAction = await dispatch(resetPwd({ data: { code, uid: userId, password: inputs.password } }));
    if (requestOK(resAction, resetPwd)) {
      const result = unwrapResult(resAction);
      onSuccess({ userId: result.apiResponseData.user_id });
    }
  };

  return (
    <Col mt={8} px={{ base: 4 }} maxW="29.625rem" w="full">
      <Logo containerProps={{ justifyContent: "center" }} />
      <UiText vr="headline3" fontSize={{ base: "1.5rem", md: "1.75rem" }} textAlign="center" mt={12}>
        Reset password
      </UiText>

      <form onSubmit={handleSubmit}>
        <Col mx="auto" mt={5}>
          <UiFormControl {...textVr("body2")} isRequired isInvalid={!!validation?.errors?.password} mt={4}>
            <UiFormLabel fontWeight="400">Password</UiFormLabel>
            <PasswordInput
              name="password"
              defaultValue={inputs.password}
              _invalid={{ boxShadow: "none", borderColor: "red" }}
              onChange={({ target }) => setInputs({ password: target.value })}
            />
            {validation?.errors?.password?.[0] && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {validation?.errors?.password?.[0]}
              </UiFormErrorMessage>
            )}
          </UiFormControl>

          <UiFormControl isRequired isInvalid={!!validation?.errors?.confirmPassword} mt={4}>
            <UiFormLabel fontWeight="400">Confirm Password</UiFormLabel>
            <PasswordInput
              placeholder="Enter your password again"
              name="password"
              defaultValue={inputs.confirmPassword}
              _invalid={{ boxShadow: "none", borderColor: "red" }}
              onChange={({ target }) => setInputs({ confirmPassword: target.value })}
            />
            {validation?.errors?.confirmPassword?.[0] && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {validation?.errors?.confirmPassword?.[0]}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
          <UiPrimaryButton
            isLoading={loading}
            type="submit"
            onClick={handleSubmit}
            isDisabled={!(inputs.password && inputs.confirmPassword)}
            mt={8}
          >
            Reset password
          </UiPrimaryButton>
        </Col>
      </form>
      <UiLink as={Link} to={{ pathname: PAGE_URLS.LOGIN, search: window.location.search }} mx="auto" mt={4}>
        <UiText vr="title1" color="navy">
          Return to Login
        </UiText>
      </UiLink>
    </Col>
  );
}
